import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { Send, Lock, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

/**
 * Settings → Email → SMTP credentials.
 *
 * Mirrors the IMAP config panel: values are written straight into
 * backend/.env on save (via POST /admin/smtp/config) so the password never
 * has to travel through chat. The current status (configured / has_password)
 * is reported by GET /admin/smtp/status — the password itself is NEVER
 * returned by the backend, only a "yes, one is set" boolean.
 */
export default function SmtpConfigPanel() {
  const toast = useToast();
  const [status, setStatus] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [form, setForm] = useState({
    host: "", port: 587, username: "",
    password: "", from_email: "", from_name: "",
    use_tls: true,
  });
  const [pwTouched, setPwTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  const load = async () => {
    setLoadError("");
    try {
      const { data } = await api.get("/admin/smtp/status");
      setStatus(data);
      setForm((f) => ({
        ...f,
        host: data.host || "",
        port: data.port || 587,
        username: data.username || "",
        from_email: data.from_email || "",
        from_name: data.from_name || "",
        use_tls: !!data.use_tls,
        password: "", // never pre-populate
      }));
      setPwTouched(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status404 = e?.response?.status === 404;
      const msg = status404
        ? "The SMTP endpoint isn't deployed in this environment yet. Redeploy the app to enable it."
        : (typeof detail === "string" ? detail : (e.message || "Failed to load SMTP status"));
      setLoadError(msg);
      toast.error(msg);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      // Only send the password if the user actually typed into the box.
      // Leaving it untouched means "keep the existing one" — the same UX
      // pattern as the IMAP modal.
      const payload = { ...form };
      if (!pwTouched) delete payload.password;
      await api.post("/admin/smtp/config", payload);
      toast.success("SMTP credentials saved.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testTo.trim()) { toast.error("Enter a recipient email first."); return; }
    setTesting(true);
    try {
      await api.post("/admin/smtp/test", { to: testTo.trim() });
      toast.success(`Test email sent to ${testTo.trim()}.`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (e.message || "Test failed"));
    } finally { setTesting(false); }
  };

  if (!status) {
    return (
      <div className="space-y-2" data-testid="smtp-config-panel-loading">
        {loadError ? (
          <div className="rounded-lg px-3 py-2 text-sm border bg-red-50 border-red-200 text-red-800 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-semibold mb-0.5">Couldn't load SMTP status</div>
              <div className="text-xs leading-snug">{loadError}</div>
              <button
                onClick={load}
                className="mt-2 text-xs font-semibold underline hover:no-underline"
                data-testid="smtp-config-panel-retry"
              >Retry</button>
            </div>
          </div>
        ) : (
          <div className="text-xs font-mono text-zinc-500 flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading SMTP status…
          </div>
        )}
      </div>
    );
  }

  const isReady = status.configured && status.has_password;

  return (
    <div className="space-y-4" data-testid="smtp-config-panel">
      {/* Status pill */}
      <div className={[
        "rounded-lg px-3 py-2 text-sm flex items-center gap-2 border",
        isReady ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                : "bg-amber-50 border-amber-200 text-amber-900",
      ].join(" ")}>
        {isReady
          ? <><CheckCircle2 className="w-4 h-4 text-emerald-600" /> SMTP is configured — outgoing email is wired up.</>
          : <><AlertTriangle className="w-4 h-4 text-amber-600" /> SMTP is not yet wired — fill the fields below to enable outgoing email.</>}
      </div>

      <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="SMTP Host" value={form.host} onChange={(v) => update("host", v)} placeholder="mail.example.com" />
        <Field label="Port" type="number" value={form.port} onChange={(v) => update("port", Number(v))} placeholder="587" />
        <Field label="Username" value={form.username} onChange={(v) => update("username", v)} placeholder="account@example.com" />
        <div>
          <label className="es-label flex items-center gap-1.5">
            <Lock className="w-3 h-3" /> Password
            {status.has_password && !pwTouched && <span className="ml-1 text-[10px] font-mono uppercase tracking-wider text-emerald-700">stored</span>}
          </label>
          <input
            type="password"
            value={form.password}
            onChange={(e) => { setPwTouched(true); update("password", e.target.value); }}
            placeholder={status.has_password ? "Leave blank to keep current password" : "Type the email account password"}
            className="es-input"
            data-testid="smtp-password-input"
            autoComplete="new-password"
          />
        </div>
        <Field label="From Email" type="email" value={form.from_email} onChange={(v) => update("from_email", v)} placeholder="defaults to Username" />
        <Field label="From Name" value={form.from_name} onChange={(v) => update("from_name", v)} placeholder="Edenstone Ops" />

        <label className="inline-flex items-center gap-2 mt-1 col-span-1 sm:col-span-2">
          <input
            type="checkbox"
            checked={!!form.use_tls}
            onChange={(e) => update("use_tls", e.target.checked)}
            data-testid="smtp-use-tls"
          />
          <span className="text-sm text-zinc-700">
            Use STARTTLS (recommended for port 587). Disable only if you use implicit SSL on port 465.
          </span>
        </label>

        <div className="col-span-1 sm:col-span-2 flex items-center justify-end gap-2 pt-1">
          <button
            type="submit"
            disabled={saving}
            className="es-btn-primary disabled:opacity-60"
            data-testid="smtp-save"
          >
            {saving ? "Saving…" : "Save SMTP settings"}
          </button>
        </div>
      </form>

      {/* Test sender */}
      <div className="border-t border-zinc-100 pt-4 flex items-end gap-2 flex-wrap" data-testid="smtp-test-row">
        <div className="flex-1 min-w-[200px]">
          <label className="es-label">Send a test email to</label>
          <input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="you@example.com"
            className="es-input"
            data-testid="smtp-test-to"
          />
        </div>
        <button
          type="button"
          disabled={testing || !isReady}
          onClick={sendTest}
          className="es-btn-secondary h-10 disabled:opacity-60 inline-flex items-center gap-1.5"
          data-testid="smtp-test-submit"
        >
          {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          {testing ? "Sending…" : "Send test"}
        </button>
        <p className="basis-full text-[11px] text-zinc-500 leading-relaxed">
          {isReady
            ? "If the test email doesn't arrive within a minute, check the recipient's spam folder, then double-check the password/host."
            : "Save the SMTP settings (including a password) before sending a test."}
        </p>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <div>
      <label className="es-label">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="es-input"
      />
    </div>
  );
}
